import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"

// Contrôleur pour gérer les prévisualisations de réservation de créneaux en temps réel
export default class extends Controller {
  static values = {
    userSessionId: String,
    agendaType: String
  }

  connect() {
    console.log("TimeslotReservation controller connecting...")

    this.consumer = createConsumer()
    this.currentPreview = null
    this.lockedSlots = new Set() // Créneaux verrouillés par d'autres utilisateurs
    this.userPreviews = new Map() // Map user_id -> {bookableId, slotTime}

    // Déterminer le type d'agenda basé sur l'URL ou les données
    this.agendaType = this.agendaTypeValue || this.detectAgendaType()

    console.log(`TimeslotReservation: agenda type detected as '${this.agendaType}'`)
    console.log(`TimeslotReservation: user session ID is '${this.userSessionIdValue}'`)

    // S'abonner au canal de prévisualisation
    this.subscription = this.consumer.subscriptions.create(
      {
        channel: "AgendaPreviewChannel",
        agenda_type: this.agendaType
      },
      {
        connected: this.handleConnected.bind(this),
        disconnected: this.handleDisconnected.bind(this),
        received: this.handleBroadcast.bind(this)
      }
    )

    console.log(`TimeslotReservation connected for agenda type: ${this.agendaType}`)
  }

  disconnect() {
    this.stopCurrentPreview()

    if (this.subscription) {
      this.subscription.unsubscribe()
    }
    if (this.consumer) {
      this.consumer.disconnect()
    }
  }

  handleConnected() {
    console.log("Connected to agenda preview channel")

    // Demander l'état initial au serveur
    this.subscription.perform("get_current_previews", {
      agenda_type: this.agendaType
    })
  }

  handleDisconnected() {
    console.log("Disconnected from agenda preview channel")
  }

  handleBroadcast(data) {
    console.log("Received broadcast:", data)

    switch (data.action) {
      case 'load_all':
        this.loadAllPreviews(data.previews)
        break
      case 'start':
        this.handlePreviewStart(data)
        break
      case 'update':
        this.handlePreviewUpdate(data)
        break
      case 'stop':
        this.handlePreviewStop(data)
        break
      default:
        console.log("Unknown broadcast action:", data.action)
    }
  }

  loadAllPreviews(previews) {
    console.log("Loading all previews:", previews)

    // Nettoyer toutes les prévisualisations existantes
    this.clearAllPreviews()

    // Obtenir la date de l'agenda affiché
    const currentDate = this.getCurrentAgendaDate()

    // Afficher chaque prévisualisation seulement si elle correspond à la date affichée
    previews.forEach(preview => {
      if (preview.user_session_id !== this.userSessionIdValue) {
        // Vérifier si la date de la prévisualisation correspond à la date de l'agenda affiché
        if (preview.date && currentDate && preview.date !== currentDate) {
          console.log(`Skipping preview for date ${preview.date} (current: ${currentDate})`)
          return
        }

        const duration = preview.duration || 20
        const requiredSlots = this.calculateRequiredSlots(preview.slot_time, duration)

        // Ajouter tous les créneaux aux slots verrouillés et les afficher
        for (const slot of requiredSlots) {
          this.lockedSlots.add(`${preview.bookable_id}_${slot}`)
          this.showPreviewHighlight(preview.bookable_id, slot, preview.user_name, false)
        }

        this.userPreviews.set(preview.user_id, {
          bookableId: preview.bookable_id,
          slotTime: preview.slot_time,
          duration: duration,
          requiredSlots: requiredSlots
        })
      }
    })
  }

  handlePreviewStart(data) {
    if (data.user_session_id === this.userSessionIdValue) {
      // C'est notre propre prévisualisation, ne pas l'afficher comme locked
      return
    }

    // Vérifier si la date de la prévisualisation correspond à la date de l'agenda affiché
    const currentDate = this.getCurrentAgendaDate()
    if (data.date && currentDate && data.date !== currentDate) {
      console.log(`Preview date (${data.date}) doesn't match current agenda date (${currentDate}), ignoring`)
      return
    }

    const duration = data.duration || 20
    const requiredSlots = this.calculateRequiredSlots(data.slot_time, duration)

    // Ajouter tous les créneaux aux slots verrouillés et les afficher
    for (const slot of requiredSlots) {
      this.lockedSlots.add(`${data.bookable_id}_${slot}`)
      this.showPreviewHighlight(data.bookable_id, slot, data.user_name, false)
    }

    this.userPreviews.set(data.user_id, {
      bookableId: data.bookable_id,
      slotTime: data.slot_time,
      duration: duration,
      requiredSlots: requiredSlots
    })
  }

  handlePreviewUpdate(data) {
    if (data.user_session_id === this.userSessionIdValue) {
      return
    }

    // Vérifier si la date de la prévisualisation correspond à la date de l'agenda affiché
    const currentDate = this.getCurrentAgendaDate()
    if (data.date && currentDate && data.date !== currentDate) {
      console.log(`Preview update date (${data.date}) doesn't match current agenda date (${currentDate}), ignoring`)
      return
    }

    // Pour une mise à jour, on doit d'abord nettoyer l'ancienne position de cet utilisateur
    this.clearUserPreview(data.user_id)

    // Puis afficher la nouvelle position avec la nouvelle durée
    const duration = data.duration || 20
    const requiredSlots = this.calculateRequiredSlots(data.slot_time, duration)

    for (const slot of requiredSlots) {
      this.lockedSlots.add(`${data.bookable_id}_${slot}`)
      this.showPreviewHighlight(data.bookable_id, slot, data.user_name, false)
    }

    this.userPreviews.set(data.user_id, {
      bookableId: data.bookable_id,
      slotTime: data.slot_time,
      duration: duration,
      requiredSlots: requiredSlots
    })
  }

  handlePreviewStop(data) {
    // Vérifier si la date de la prévisualisation correspond à la date de l'agenda affiché
    const currentDate = this.getCurrentAgendaDate()

    if (data.date && currentDate && data.date !== currentDate) {
      console.log(`Preview stop date mismatch: ${data.date} vs ${currentDate}, ignoring`)
      return
    }

    // Toujours nettoyer la preview de l'utilisateur, même si c'est la nôtre
    // car d'autres clients doivent voir que notre preview s'est arrêtée
    this.clearUserPreview(data.user_id)
  }

  // Méthode appelée quand l'utilisateur clique sur un créneau
  startPreview(bookableId, slotTime, duration = 20) {
    console.log(`startPreview called with bookableId=${bookableId}, slotTime=${slotTime}, duration=${duration}`)

    // Calculer tous les créneaux nécessaires pour cette durée
    const requiredSlots = this.calculateRequiredSlots(slotTime, duration)
    console.log(`Required slots for ${duration} minutes:`, requiredSlots)

    // Vérifier que tous les créneaux nécessaires sont libres
    for (const slot of requiredSlots) {
      const slotKey = `${bookableId}_${slot}`
      if (this.lockedSlots.has(slotKey)) {
        console.log(`Slot ${slot} is locked by another user`)
        return false
      }
    }

    // Arrêter la prévisualisation précédente s'il y en a une
    this.stopCurrentPreview()

    // Créer la nouvelle prévisualisation
    this.currentPreview = { bookableId, slotTime, duration, requiredSlots }

    // Afficher la surbrillance pour tous les créneaux nécessaires
    for (const slot of requiredSlots) {
      this.showPreviewHighlight(bookableId, slot, "Vous", true)
    }

    // Envoyer au serveur
    console.log("Sending start_preview to server...")
    this.subscription.perform("start_preview", {
      agenda_type: this.agendaType,
      bookable_id: bookableId,
      slot_time: slotTime,
      duration: duration,
      user_session_id: this.userSessionIdValue,
      date: this.getCurrentAgendaDate()
    })

    return true
  }

  // Action Stimulus appelée lors du clic sur un créneau
  slotClicked(event) {
    // Extraire les informations du créneau depuis les data attributes
    const slotElement = event.currentTarget.closest('[data-time]')
    if (!slotElement) return

    const bookableId = slotElement.closest('[data-bookable-id]')?.dataset.bookableId
    const slotTime = slotElement.dataset.time

    if (!bookableId || !slotTime) return

    // Vérifier si ce créneau fait partie d'une preview existante
    if (this.isSlotInPreview(bookableId, slotTime)) {
      console.log(`Slot ${bookableId}_${slotTime} is in preview, ignoring click`)
      event.preventDefault()
      event.stopPropagation()
      return false
    }

    // Démarrer la prévisualisation
    const success = this.startPreview(bookableId, slotTime)

    if (success) {
      // Continuer avec l'ouverture de la modale (action par défaut du lien)
      return true
    } else {
      // Empêcher l'ouverture de la modale si le créneau est verrouillé
      event.preventDefault()
      event.stopPropagation()
      return false
    }
  }

  // Méthode appelée quand l'utilisateur change de créneau (drag & drop)
  updatePreview(bookableId, slotTime, duration = 20) {
    console.log(`updatePreview called with bookableId=${bookableId}, slotTime=${slotTime}, duration=${duration}`)

    if (!this.currentPreview) {
      console.log("No current preview, starting new one")
      return this.startPreview(bookableId, slotTime, duration)
    }

    // Calculer tous les créneaux nécessaires pour cette durée
    const requiredSlots = this.calculateRequiredSlots(slotTime, duration)
    console.log(`Required slots for ${duration} minutes:`, requiredSlots)

    // Vérifier que tous les nouveaux créneaux sont libres
    for (const slot of requiredSlots) {
      const slotKey = `${bookableId}_${slot}`
      if (this.lockedSlots.has(slotKey)) {
        console.log(`Target slot ${slot} is locked by another user`)
        return false
      }
    }

    // Cacher l'ancienne surbrillance (tous les créneaux de l'ancienne preview)
    if (this.currentPreview.requiredSlots) {
      for (const slot of this.currentPreview.requiredSlots) {
        this.hidePreviewHighlight(this.currentPreview.bookableId, slot)
      }
    } else {
      // Fallback pour l'ancien format
      this.hidePreviewHighlight(this.currentPreview.bookableId, this.currentPreview.slotTime)
    }

    // Mettre à jour la prévisualisation
    this.currentPreview = { bookableId, slotTime, duration, requiredSlots }

    // Afficher la nouvelle surbrillance pour tous les créneaux nécessaires
    for (const slot of requiredSlots) {
      this.showPreviewHighlight(bookableId, slot, "Vous", true)
    }

    // Envoyer au serveur
    console.log("Sending update_preview to server...")
    this.subscription.perform("update_preview", {
      agenda_type: this.agendaType,
      bookable_id: bookableId,
      slot_time: slotTime,
      duration: duration,
      user_session_id: this.userSessionIdValue,
      date: this.getCurrentAgendaDate()
    })

    return true
  }

  // Méthode appelée quand l'utilisateur annule ou termine sa réservation
  stopPreview() {
    this.stopCurrentPreview()
  }

  stopCurrentPreview() {
    if (this.currentPreview) {
      console.log("stopCurrentPreview called")

      // Cacher la surbrillance pour tous les créneaux
      if (this.currentPreview.requiredSlots) {
        for (const slot of this.currentPreview.requiredSlots) {
          this.hidePreviewHighlight(this.currentPreview.bookableId, slot)
        }
      } else {
        // Fallback pour l'ancien format
        this.hidePreviewHighlight(this.currentPreview.bookableId, this.currentPreview.slotTime)
      }

      // Envoyer au serveur
      this.subscription.perform("stop_preview", {
        agenda_type: this.agendaType
      })

      this.currentPreview = null
    }
  }

  // Calculer tous les créneaux nécessaires pour une durée donnée
  calculateRequiredSlots(startSlotTime, duration) {
    const slots = []
    const slotDurationMinutes = 5 // Chaque slot = 5 minutes
    const numberOfSlots = Math.ceil(duration / slotDurationMinutes)

    // Convertir le temps de début en minutes depuis minuit
    const startHour = parseInt(startSlotTime.substring(0, 2))
    const startMinute = parseInt(startSlotTime.substring(2, 4))
    let currentMinutes = startHour * 60 + startMinute

    for (let i = 0; i < numberOfSlots; i++) {
      const slotHour = Math.floor(currentMinutes / 60)
      const slotMinute = currentMinutes % 60
      const slotTime = `${slotHour.toString().padStart(2, '0')}${slotMinute.toString().padStart(2, '0')}`
      slots.push(slotTime)
      currentMinutes += slotDurationMinutes
    }

    return slots
  }

  // Vérifier si un créneau fait partie d'une preview existante
  isSlotInPreview(bookableId, slotTime) {
    // Vérifier si c'est notre propre preview
    if (this.currentPreview && this.currentPreview.bookableId === bookableId) {
      if (this.currentPreview.requiredSlots) {
        return this.currentPreview.requiredSlots.includes(slotTime)
      } else {
        // Fallback pour l'ancien format
        return this.currentPreview.slotTime === slotTime
      }
    }

    // Vérifier les previews des autres utilisateurs
    for (const [userId, userPreview] of this.userPreviews) {
      if (userPreview.bookableId === bookableId) {
        if (userPreview.requiredSlots) {
          if (userPreview.requiredSlots.includes(slotTime)) {
            return true
          }
        } else {
          // Fallback pour l'ancien format
          if (userPreview.slotTime === slotTime) {
            return true
          }
        }
      }
    }

    return false
  }

  // Vérifier si un créneau est disponible pour réservation
  isSlotAvailable(bookableId, slotTime) {
    const slotKey = `${bookableId}_${slotTime}`
    return !this.lockedSlots.has(slotKey)
  }

  // Afficher la surbrillance de prévisualisation
  showPreviewHighlight(bookableId, slotTime, userName, isOwn) {
    const slotElement = this.findSlotElement(bookableId, slotTime)
    if (!slotElement) {
      console.warn(`Slot element not found: ${bookableId}_${slotTime}`)
      return
    }

    // Supprimer les classes de prévisualisation existantes
    this.removePreviewClasses(slotElement)

    // Ajouter les classes appropriées
    if (isOwn) {
      slotElement.classList.add('preview-own', 'bg-red-100', 'cursor-not-allowed')
    } else {
      slotElement.classList.add('preview-other', 'bg-red-100', 'cursor-not-allowed')
    }

    // Ajouter un indicateur visuel avec le nom de l'utilisateur
    let indicator = slotElement.querySelector('.preview-indicator')
    if (!indicator) {
      indicator = document.createElement('div')
      indicator.classList.add('preview-indicator', 'absolute', 'inset-0', 'flex', 'items-center', 'justify-center', 'text-xs', 'font-medium', 'pointer-events-none', 'z-20')
      slotElement.appendChild(indicator)
    }

    indicator.classList.toggle('text-blue-700', isOwn)
    indicator.classList.toggle('text-yellow-700', !isOwn)
  }

  // Cacher la surbrillance de prévisualisation
  hidePreviewHighlight(bookableId, slotTime) {
    const slotElement = this.findSlotElement(bookableId, slotTime)
    if (!slotElement) {
      return
    }

    this.removePreviewClasses(slotElement)

    // Supprimer l'indicateur
    const indicator = slotElement.querySelector('.preview-indicator')
    if (indicator) {
      indicator.remove()
    }
  }

  // Nettoyer toutes les prévisualisations
  clearAllPreviews() {
    this.element.querySelectorAll('.preview-own, .preview-other').forEach(element => {
      this.removePreviewClasses(element)
      const indicator = element.querySelector('.preview-indicator')
      if (indicator) {
        indicator.remove()
      }
    })

    this.lockedSlots.clear()
    this.userPreviews.clear()
  }

  // Nettoyer la prévisualisation d'un utilisateur spécifique
  clearUserPreview(userId) {
    const userPreview = this.userPreviews.get(userId)
    if (userPreview) {
      // Gérer les créneaux multiples si disponibles
      if (userPreview.requiredSlots) {
        for (const slot of userPreview.requiredSlots) {
          const slotKey = `${userPreview.bookableId}_${slot}`
          this.lockedSlots.delete(slotKey)
          this.hidePreviewHighlight(userPreview.bookableId, slot)
        }
      } else {
        // Fallback pour l'ancien format
        const slotKey = `${userPreview.bookableId}_${userPreview.slotTime}`
        this.lockedSlots.delete(slotKey)
        this.hidePreviewHighlight(userPreview.bookableId, userPreview.slotTime)
      }

      this.userPreviews.delete(userId)
    }
  }

  // Utilitaire pour supprimer les classes de prévisualisation
  removePreviewClasses(element) {
    element.classList.remove(
      'preview-own', 'preview-other',
      'bg-red-100', 'bg-red-100',
      'cursor-not-allowed'
    )
  }

  // Trouver l'élément DOM correspondant à un créneau
  findSlotElement(bookableId, slotTime) {
    // L'ID des créneaux suit le format : appointment_slot_{bookable_id}_{time}
    const slotId = `appointment_slot_${bookableId}_${slotTime}`
    return document.getElementById(slotId)
  }

  // Détecter le type d'agenda basé sur l'URL ou d'autres indices
  detectAgendaType() {
    const path = window.location.pathname
    if (path.includes('treatments')) {
      return 'treatments'
    }
    return 'sunbeds' // défaut
  }

  // Obtenir la date de l'agenda actuellement affiché
  getCurrentAgendaDate() {
    // Méthode 1: Chercher le contrôleur schedule qui contient la date de l'agenda
    const scheduleElement = document.querySelector('[data-controller*="schedule"]')
    if (scheduleElement) {
      const scheduleController = this.application.getControllerForElementAndIdentifier(scheduleElement, "schedule")
      if (scheduleController && scheduleController.dateValue) {
        // La date du contrôleur schedule est au format ISO, on extrait juste la partie date
        const agendaDate = scheduleController.dateValue.split('T')[0]
        console.log(`Getting current agenda date from schedule controller: ${agendaDate}`)
        return agendaDate
      }
    }

    // Méthode 2: Chercher dans les éléments time avec datetime
    const timeElement = document.querySelector('time[datetime]')
    if (timeElement) {
      const datetime = timeElement.getAttribute('datetime')
      if (datetime) {
        const agendaDate = datetime.split('T')[0]
        console.log(`Getting current agenda date from time element: ${agendaDate}`)
        return agendaDate
      }
    }

    // Méthode 3: Extraire depuis l'URL si elle contient un paramètre de date
    const urlParams = new URLSearchParams(window.location.search)
    const dateParam = urlParams.get('date')
    if (dateParam) {
      console.log(`Getting current agenda date from URL param: ${dateParam}`)
      return dateParam
    }

    // Fallback: date actuelle au format ISO
    const today = new Date().toISOString().split('T')[0]
    console.log(`Using fallback date (today): ${today}`)
    return today
  }
}
